<template>
  <panel-layout
    id="stockTake-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="stockTake && !stockTake.isNewRecord" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ stockTake.isNewRecord ? $t('stockTake.new') : $t('pages.stockTake.edit.description', { description: stockTake.description }) }}
          </h2>
          <div v-if="!stockTake.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200">
              {{ $date.format(stockTake.created * 1000, $date.presets.long) }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <template #actions-right>
      <div v-if="stockTake && !stockTake.isNewRecord && stockTake.isStatusOpen" class="flex items-center space-x-1">
        <o-tooltip class="h-8" :text="$t('general.apply')">
          <o-button
            size="slim"
            icon="applyStocktake"
            variant="success"
            flat
            @click="handleApply"
          />
        </o-tooltip>
        <o-tooltip class="h-8" :text="$t('pages.stockTake.edit.importCSV')">
          <input ref="input" type="file" accept=".csv" class="w-px h-px opacity-0 overflow-hidden absolute" @change="handleCSVUpload">
          <o-button
            size="slim"
            icon="download"
            flat
            @click="handleImportClick"
          />
        </o-tooltip>
        <o-tooltip class="h-8" :text="$t('pages.stockTake.edit.printForm')">
          <o-button
            size="slim"
            icon="print"
            flat
            @click="handlePrint"
          />
        </o-tooltip>
      </div>
    </template>

    <div v-if="stockTake && stockTake.isNewRecord" class="h-full w-full">
      <o-scroll>
        <div class="w-full px-6 py-2 max-w-xs mx-auto mt-20">
          <h2 class="text-center text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ $t('stockTake.new') }}
          </h2>
          <p class="text-center text-gray-700 text-sm md:text-base mt-1 mb-12 dark:text-gray-200">
            {{ $t('pages.stockTake.edit.new') }}
          </p>

          <o-form ref="formRef">
            <o-field-group :label="$t('general.user')" class="w-full mt-6 block">
              <employee-select
                v-model="stockTake.employee"
                :types="['employee', 'admin']"
                class="w-full"
              />
            </o-field-group>

            <o-field-group v-slot="{ state }" :label="$t('general.description')" class="w-full mt-6 block" rules="required">
              <text-field v-model="stockTake.description" :state="state" />
            </o-field-group>
          </o-form>

          <div class="flex justify-center mt-6">
            <o-button :disabled="!stockTake.description || !stockTake.description.length" variant="success" @click="handleSaveNew()">
              {{ $t('stockTake.new') }}
            </o-button>
          </div>
        </div>
      </o-scroll>
    </div>

    <div v-if="stockTake && !stockTake.isNewRecord" class="h-full w-full">
      <o-scroll>
        <div class="w-full px-6 py-2">
          <o-description-details v-if="stockTake.employee" :term="$t('general.employee')">
            <div class="flex items-center mr-4 mb-1 leading-tight">
              <o-avatar :items="[ stockTake.employee ]" class="hidden md:flex pr-2" />
              <p>
                {{ stockTake.employee.displayName }}
              </p>
            </div>
          </o-description-details>

          <o-description-details :term="$t('general.status')">
            <o-chip :variant="stockTake.isStatusOpen ? 'warning' : 'success'">
              {{ $t(StockTakeStatusLabels[stockTake.status as StockTakeStatus]) }}
            </o-chip>
          </o-description-details>

          <o-table
            v-if="stockTake.isStatusOpen && staging"
            class="py-5 w-full"
            container-class="border rounded bg-white dark:bg-gray-900 dark:border-gray-800 overflow-hidden"
            v-model:data="filtered"
            :headers="[
              {text: $t('general.product')},
              {text: $t('general.price')},
              {text: $t('pages.stockTake.edit.systemInventory')},
              {text: $t('pages.stockTake.edit.inventory')},
              {text: $t('general.note')}
            ]"
            :tbody-class="{
              tfoot: '',
              tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
              td: 'py-3 px-4 text-gray-800 text-sm dark:text-gray-100',
              stripe: 'bg-gray-50/50 dark:bg-gray-900'
            }"
            :thead-class="{
              thead: 'border-b',
              tr: 'border-blue-700',
              th: 'font-normal p-4 pb-3 bg-gray-50 text-gray-700 text-sm sticky top-0 dark:bg-gray-900 dark:text-gray-200',
              content: 'flex item-center'
            }"
            :limit="20"
            striped
          >
            <template #header-left>
              <div class="flex items-center">
                <div class="w-full md:w-auto">
                  <text-field v-model="filters.query" leading="search" :placeholder="$t('general.filter')" class="w-full md:w-50" />
                </div>
              </div>
            </template>

            <template #header-right>
              <div class="flex items-center space-x-2">
                <select-field v-model="filters.brand" :options="brandOptions" :placeholder="$t('general.brand')" />
                <select-field v-model="filters.supplier" :options="supplierOptions" :placeholder="$t('general.supplier')" />
                <select-field v-model="filters.category" :options="categoryOptions" :placeholder="$t('general.category')" />
              </div>
            </template>

            <template #row="props">
              <td :class="props.tdClass">
                <p>
                  {{ props.row.product ? props.row.product.name : '-' }}
                </p>
                <p v-if="props.row.product" class="text-gray-500 text-sm dark:text-gray-400">
                  {{ props.row.product.sku }}
                </p>
              </td>
              <td :class="props.tdClass">
                {{ props.row.product ? props.row.product.priceLabel : '-' }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.product ? props.row.product.inventory : '-' }}
              </td>
              <td :class="props.tdClass">
                <number-field v-model="props.row.inventory" slim />
              </td>
              <td :class="props.tdClass">
                <text-field v-model="props.row.notes" />
              </td>
            </template>
          </o-table>

          <div v-if="stockTake.isStatusApplied && products">
            <o-table
              class="py-5 w-full"
              container-class="border rounded bg-white dark:bg-gray-900 dark:border-gray-800 overflow-hidden"
              v-model:data="stockTake.products"
              :headers="[
                {text: $t('general.product')},
                {text: $t('general.note')},
                {text: $t('pages.stockTake.edit.systemInventory')},
                {text: $t('pages.stockTake.edit.inventory')},
                {text: $t('pages.stockTake.edit.priceDifference')}
              ]"
              :tbody-class="{
                tfoot: '',
                tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
                td: 'py-3 px-4 text-gray-800 text-sm dark:text-gray-100',
                stripe: 'bg-gray-50/50 dark:bg-gray-900'
              }"
              :thead-class="{
                thead: 'border-b',
                tr: 'border-blue-700',
                th: 'font-normal p-4 pb-3 bg-gray-50 text-gray-700 text-sm sticky top-0 dark:bg-gray-900 dark:text-gray-200',
                content: 'flex item-center'
              }"
              :limit="20"
              striped
            >
              <template #row="props">
                <td :class="props.tdClass">
                  {{ productsHash[props.row.idProduct].name }}
                </td>
                <td :class="props.tdClass">
                  {{ props.row.notes }}
                </td>
                <td :class="props.tdClass">
                  {{ props.row.systemInventory }}
                </td>
                <td :class="props.tdClass">
                  {{ props.row.inventory }}
                </td>
                <td :class="props.tdClass">
                  {{ Number(props.row.systemWholesalePrice)* ( props.row.systemInventory - props.row.inventory ) * -1 }}
                </td>
              </template>

              <template #tfoot>
                <tr>
                  <td colspan="5" class="bg-gray-100 dark:bg-gray-900">
                    <p class="text-right px-3 py-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                      {{ $t('pages.stockTake.edit.totalDifference', { totalPriceDifference }) }}
                    </p>
                  </td>
                </tr>
              </template>
            </o-table>
          </div>
        </div>
      </o-scroll>
    </div>

    <template #footer>
      <div v-if="stockTake && !stockTake.isNewRecord && stockTake.isStatusOpen">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <apply-dialog
      ref="applyDialog"
      :portal="panel.portal"
      @success="applySuccess"
    />

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
import { StockTakeStatus, StockTakeStatusLabels } from '~/_api/models/stockTake'
// papaparse lazy-loaded inside handleCSVUpload (below) — only needed on file
// upload, not on panel mount. See .docs/performance.md.
import Fuse from 'fuse.js'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import EmployeeSelect from '@/components/employee/elements/Select.vue'
// @ts-ignore
import ApplyDialog from '@/components/stockTake/actions/Apply.vue'

import {
  ODescriptionDetails,
  OTable,
  OAvatar,
} from '@/components/elements'

import {
  TextField,
  NumberField,
  ToggleField,
  SelectField,
} from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $date, $currency, $i18n } = useNuxtApp()

const employeeStore = useEmployeeStore()
const productStore = useProductStore()

const stockTake = ref<any>(null)
const products = ref<any>(null)
const staging = ref<any>(null)
const filters = ref({
  query: '',
  category: null as any,
  supplier: null as any,
  brand: null as any,
})
const errors = ref<any>(false)
const loading = ref(true)
const missingProduct = ref(false)

const confirmDialog = ref<any>()
const applyDialog = ref<any>()
const input = ref<any>()
const formRef = ref<any>()

const brandOptions = computed(() => {
  const _products = staging.value
  const brands: Record<string, any> = {}

  for (const { product } of _products) {
    const brand = product.brand

    if (brand) {
      brands[brand.idProductBrand] = {
        label: brand.name,
        value: brand,
      }
    }
  }

  return Object.values(brands)
})

const supplierOptions = computed(() => {
  const _products = staging.value
  const suppliers: Record<string, any> = {}

  for (const { product } of _products) {
    const supplier = product.supplier

    if (supplier) {
      suppliers[supplier.idSupplier] = {
        label: supplier.name,
        value: supplier,
      }
    }
  }

  return Object.values(suppliers)
})

const categoryOptions = computed(() => {
  const _products = staging.value
  const categories: Record<string, any> = {}

  for (const { product } of _products) {
    const category = product.category

    if (category) {
      categories[category.idProductCategory] = {
        label: category.name,
        value: category,
      }
    }
  }

  return Object.values(categories)
})

const filtered = computed(() => {
  const _filters = filters.value
  let _staging = staging.value

  if (_filters.query) {
    const query = _filters.query
    const fuse = new Fuse(_staging, {
      keys: [
        'product.name',
        'product.category.name',
        'product.supplier.name',
        'product.information',
        'notes',
      ],
    })

    const results = fuse.search(query)
    _staging = results.map(({ item }: any) => item)
  }

  if (_filters.brand) {
    const brand = _filters.brand
    _staging = _staging.filter(({ product }: any) => product.brand?.idProductBrand === brand.idProductBrand)
  }

  if (_filters.supplier) {
    const supplier = _filters.supplier
    _staging = _staging.filter(({ product }: any) => product.supplier?.idSupplier === supplier.idSupplier)
  }

  if (_filters.category) {
    const category = _filters.category
    _staging = _staging.filter(({ product }: any) => product.category?.idProductCategory === category.idProductCategory)
  }

  return _staging
})

const { unsavedChanges, snapshot } = useUnsavedChanges(stockTake)

const productsHash = computed(() => {
  return Object.fromEntries(products.value.map((product: any) => [product.idProduct, product]))
})

const totalPriceDifference = computed(() => {
  const differences = stockTake.value.products.map((product: any) => Number(product.systemWholesalePrice) * (Number(product.systemInventory) - Number(product.inventory)) * -1)
  const total = differences.reduce((a: number, b: number) => a + b, 0)

  return $currency.format(total)
})

watch(products, () => {
  setProducts()
})

watch(staging, () => {
  if (stockTake.value.isStatusOpen) {
    const _products = [...staging.value]
    const items = _products
      .filter((product: any) => product.inventory !== null)
      .map((product: any) => {
        const item = { ...product }
        delete item.product

        return item
      })

    stockTake.value.products = items
  }
}, { deep: true })

const fetchProducts = async () => {
  await productStore.sync()
  const categories = productStore.all

  const _products = categories
    .map((category: any) => category.products)
    .flat()

  products.value = _products
}

const setProducts = () => {
  const items = Object.fromEntries(stockTake.value.products.map((product: any) => [product.idProduct, product]))

  staging.value = products.value
    .map((product: any) => {
      if (items[product.idProduct]) {
        return {
          product,
          ...items[product.idProduct],
        }
      }

      return {
        idProduct: product.idProduct,
        inventory: null,
        notes: '',
        product,
      }
    })
}

const handleSaveNew = async () => {
  const valid = await formRef.value.validate()

  if (valid) {
    save()
  }
}

const save = async (close = false) => {
  loading.value = true
  errors.value = false

  try {
    const isNewRecord = stockTake.value.isNewRecord
    stockTake.value.idEmployee = stockTake.value.employee?.idEmployee
    const _stockTake = (await $api.stockTake.save(stockTake.value)).data

    if (_stockTake) {
      stockTake.value = _stockTake
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.stockTake.edit.success'),
        text: $i18n.t('pages.stockTake.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('StockTake', _stockTake)

      if (close) {
        panel.close()
      } else if (isNewRecord) {
        // Swap in-place to the freshly-created stock take's edit view.
        panel.replace('stock-take/edit', { id: String(_stockTake.idStockTake) })
      }
    }
  } catch (e: any) {
    errors.value = e?.errors
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const beforeunload = (e: BeforeUnloadEvent) => {
  if (unsavedChanges.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}

const handleApply = () => {
  applyDialog.value.open(stockTake.value)
}

const handleImportClick = () => {
  input.value.click()
}

const handleCSVUpload = async (e: any) => {
  const file = e.target.files[0]
  if (file) {
    const { default: Papa } = await import('papaparse')
    Papa.parse(file, {
      header: true,
      complete: processCSVData,
      error: (error: any) => {
        console.error($i18n.t('pages.stockTake.edit.errorParsing'), error)
      },
    })
  }
  e.target.value = null
}

const processCSVData = (results: any) => {
  missingProduct.value = false
  const data = results.data

  data.forEach((row: any) => {
    updateInventory(row)
  })

  $notification.toast({
    title: $i18n.t('pages.stockTake.edit.csvSuccess'),
    variant: 'success',
  })

  if (missingProduct.value) {
    $notification.toast({
      title: $i18n.t('pages.stockTake.edit.someNotFound'),
      text: $i18n.t('pages.stockTake.edit.someNotFoundInfo'),
      variant: 'warning',
    })
  }
}

const updateInventory = (row: any) => {
  const { sku, barcode, idProduct, inventory, notes } = row
  const parsedInventory = parseInt(inventory)
  const parsedIdProduct = parseInt(idProduct)

  if (!sku && !barcode && !idProduct) {
    return
  }

  const foundProduct = findProduct(parsedIdProduct, sku, barcode)

  if (foundProduct) {
    const productIndex = stockTake.value.products.findIndex(
      (product: any) => parseInt(product.idProduct) === foundProduct.idProduct,
    )

    if (productIndex !== -1) {
      stockTake.value.products[productIndex] = {
        ...stockTake.value.products[productIndex],
        inventory: parsedInventory,
        notes: notes || stockTake.value.products[productIndex].notes || '',
      }
    } else {
      stockTake.value.products.push({
        idProduct: foundProduct.idProduct,
        inventory: parsedInventory,
        notes: notes || '',
      })
    }

    staging.value = staging.value.map((product: any) =>
      parseInt(product.idProduct) === foundProduct.idProduct
        ? {
            ...product,
            inventory: parsedInventory,
            notes: notes || product.notes || '',
          }
        : product,
    )
  } else {
    missingProduct.value = true
    console.warn($i18n.t('pages.stockTake.edit.productNotFound'), idProduct || sku || barcode)
  }
}

const findProduct = (idProduct: any, sku: any, barcode: any) => {
  return products.value.find(
    (product: any) => (idProduct && product.idProduct === idProduct) || (sku && product.sku === sku) || (barcode && product.barcode === barcode),
  )
}

const handlePrint = async () => {
  const _stockTake = stockTake.value
  const _filtered = filtered.value

  const _products = _filtered.map((product: any) => {
    return [
      product.product.name || '-',
      product.product.priceLabel || '-',
      product.product.inventory || '-',
      product.inventory ? `${product.inventory}\n\n\n` : '\n\n\n',
      product.notes || '\n\n\n',
    ]
  })

  const doc = {
    content: [
      {
        text: 'Stock take',
        style: 'header',
        margin: [0, 0, 0, 10],
      },
      {
        text: [
          'Date: ',
          { text: $date.format(_stockTake.created * 1000, $date.presets.long), bold: true },
        ],
        style: 'small',
      },
      {
        text: [
          'Number: ',
          { text: _stockTake.idStockTake, bold: true },
        ],
        style: 'small',
        margin: [0, 0, 0, 10],
      },
      {
        table: {
          headerRows: 1,
          body: [
            ['Product', 'Price', 'System inventory', 'Stock take inventory', 'Note'],
            ..._products,
          ],
          widths: ['*', '*', '*', 50, 200],
          style: 'small',
        },
        margin: [0, 0, 0, 20],
      },
    ],
    styles: {
      header: {
        fontSize: 18,
        bold: true,
      },
      subheader: {
        fontSize: 15,
        bold: true,
      },
      small: {
        fontSize: 9,
      },
    },
    defaultStyle: {
      fontSize: 9,
    },
    pageMargins: [15, 15, 15, 15],
  }

  const pdfMake = await usePdfMake()
  pdfMake.createPdf(doc).open()
}

const applySuccess = (_stockTake: any) => {
  stockTake.value = _stockTake
  snapshot()

  $bus.emit.updated('StockTake', _stockTake)
}

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      stockTake.value = (await $api.stockTake.defaults({} as any)).data
      stockTake.value.employee = null

      await employeeStore.sync()
    } else {
      stockTake.value = (await $api.stockTake.get(id)).data
    }

    snapshot()
    fetchProducts()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })
onMounted(() => window.addEventListener('beforeunload', beforeunload))

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', beforeunload)
})
</script>
